import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReturnType } from "../../../types/returnManagement";
import returnRequestService from "../../../services/ReturnRequestService";
import saleOrderServices, {
  parseCatalogProducts,
} from "../../../services/SalesOrderService";
import InitiateReturn from "../../../assets/images/initiateReturn.svg";
import InitiateModal from "./InitiateModal";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { localizeReturnReason } from "../../../helper/localizeReturnReason";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchField from "../../component/common/searchField";
import { NJIM } from "../../../theme/njimTheme";

type FilterKey = "all" | "pending" | "approved" | "rejected";

const ReturnsManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [returnData, setReturnData] = useState<ReturnType[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [openInitiateModal, setOpenInitiateModal] = useState(false);
  const [priceByItemId, setPriceByItemId] = useState<Record<string, number>>({});
  const [productNameMap, setProductNameMap] = useState<Record<string, string>>({});
  const [expandedReturnId, setExpandedReturnId] = useState<string | false>(false);

  const [filters] = useState({
    pageNumber: 1,
    pageSize: 1000,
    sortBy: "RequestDate",
    sortDescending: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchProductMaps();
  }, []);

  const fetchReturnList = useCallback(async (keyword: string = "") => {
    try {
      const params = {
        keyword: keyword,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        sortBy: filters.sortBy,
        sortDescending: filters.sortDescending,
      };
      setLoading(true);
      const response = await returnRequestService.getReturnList(params);
      const finalResponse = response?.data.items;
      // Customer-initiated requests are numbered RR-*. Sage pull creates a second
      // document (e.g. HADAT-SCN-*) after posting — keep those off the customer portal.
      const customerReturns = (finalResponse ?? []).filter((item: ReturnType) =>
        /^RR-/i.test(String(item.returnNumber ?? item.title ?? "").trim())
      );
      setReturnData(customerReturns);
      localStorage.setItem(
        "sharedReturnsCache",
        JSON.stringify(customerReturns)
      );
    } catch (error) {
      console.error("Error fetching Return List:", error);
    } finally {
      setLoading(false);
    }
  }, [filters.pageNumber, filters.pageSize, filters.sortBy, filters.sortDescending]);

  useEffect(() => {
    fetchReturnList(debouncedSearchTerm);
  }, [filters, debouncedSearchTerm, fetchReturnList]);

  const fetchProductMaps = async () => {
    try {
      const response = await saleOrderServices.getProductCatalog();
      const products = parseCatalogProducts(response?.data);

      const nameMap: Record<string, string> = {};
      const priceMap: Record<string, number> = {};

      products.forEach((p) => {
        const id = String(p.id ?? p.Id ?? "").trim();
        const code = String(p.code ?? p.Code ?? "").trim();
        const name = String(p.name ?? p.Name ?? p.title ?? p.Title ?? "").trim();
        const price = Number(p.price ?? p.Price ?? 0);

        if (id) {
          if (name) nameMap[id] = name;
          if (price) priceMap[id] = price;
        }
        if (code) {
          if (name) nameMap[code] = name;
          if (price) priceMap[code] = price;
        }
      });

      setProductNameMap(nameMap);
      setPriceByItemId(priceMap);
    } catch (error) {
      console.error("Error fetching product data:", error);
      setProductNameMap({});
      setPriceByItemId({});
    }
  };

  const getDisplayAmount = (returns: ReturnType) => {
    const apiAmount = Number(returns.amount ?? 0);
    if (apiAmount > 0) return apiAmount;

    const items = returns.items ?? [];
    return items.reduce((sum, item) => {
      const price = priceByItemId[String(item.itemId ?? "").trim()] ?? 0;
      const qty = Number(item.quantity ?? 0);
      return sum + price * qty;
    }, 0);
  };

  const getStatusStyles = (status: string | number | undefined) => {
    const statusString = String(status).toLowerCase();
    if (statusString === "pending" || statusString === "initiated") {
      return {
        background: "rgba(245,158,11,0.08)",
        color: NJIM.warning,
        border: "1px solid rgba(245,158,11,0.2)",
      };
    }
    if (statusString === "approved" || statusString === "completed") {
      return {
        background: "rgba(5,150,105,0.08)",
        color: NJIM.success,
        border: "1px solid rgba(5,150,105,0.2)",
      };
    }
    if (statusString === "overdue" || statusString === "rejected") {
      return {
        background: "rgba(220,38,38,0.08)",
        color: NJIM.danger,
        border: "1px solid rgba(220,38,38,0.2)",
      };
    }
    return {
      background: NJIM.mintWash,
      color: NJIM.ink,
      border: "1px solid rgba(93,183,167,0.25)",
    };
  };

  const normalizeStatus = (status: string | number | undefined): FilterKey | "other" => {
    const enumMap: Record<string, FilterKey> = {
      "0": "pending", "1": "pending", "2": "approved", "3": "rejected", "4": "rejected",
      "initiated": "pending", "pending": "pending",
      "approved": "approved", "completed": "approved",
      "rejected": "rejected", "cancelled": "rejected",
    };
    const raw = String(status ?? "").trim().toLowerCase();
    return enumMap[raw] ?? "other";
  };

  const counts = useMemo(() => {
    const result = { total: returnData.length, pending: 0, approved: 0, rejected: 0 };
    returnData.forEach((r) => {
      const s = normalizeStatus(r.status);
      if (s === "pending") result.pending++;
      else if (s === "approved") result.approved++;
      else if (s === "rejected") result.rejected++;
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnData]);

  const filteredReturns = useMemo(() => {
    const byStatus = activeFilter === "all"
      ? returnData
      : returnData.filter((r) => normalizeStatus(r.status) === activeFilter);
    if (!debouncedSearchTerm) return byStatus;
    const kw = debouncedSearchTerm.toLowerCase();
    return byStatus.filter(
      (r) =>
        (r.returnNumber ?? "").toLowerCase().includes(kw) ||
        (r.title ?? "").toLowerCase().includes(kw)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnData, activeFilter, debouncedSearchTerm]);

  const getStatusLabel = (status: string | number | undefined): string => {
    const normalized = normalizeStatus(status);
    if (normalized === "pending") return t("returnsManagement.pending");
    if (normalized === "approved") return t("returnsManagement.approved");
    if (normalized === "rejected") return t("returnsManagement.rejected");
    return String(status ?? "").replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  const formatDate = (date?: string) =>
    date ? new Date(date).toISOString().split("T")[0] : "-";

  const handleExpand =
    (panelId: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedReturnId(isExpanded ? panelId : false);
    };
  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, letterSpacing: "-0.3px", fontSize: "1.5rem" }}>
              {t("returnsManagement.title")}
            </Typography>
            <Typography sx={{ color: NJIM.textSecondary, mb: 3, fontSize: "15px" }}>
              {t("returnsManagement.manageReturns")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            className="njim-primary-btn"
            onClick={() => setOpenInitiateModal(true)}
            sx={{
              fontSize: "13px",
              px: 2.5,
              py: 1,
              gap: 0.75,
            }}
          >
            <img src={InitiateReturn} alt="" style={{ opacity: 0.85 }} />
            {t("returnsManagement.initiateReturn")}
          </Button>
        </Box>

        {(() => {
          const cardDefs: { key: FilterKey; label: string; count: number; color: string; bg: string; border: string }[] = [
            { key: "all",      label: t("returnsManagement.totalReturns"),   count: counts.total,    color: NJIM.ink, bg: NJIM.mintWash, border: "rgba(93,183,167,0.25)" },
            { key: "pending",  label: t("returnsManagement.pendingApproval"), count: counts.pending, color: NJIM.warning, bg: "rgba(245,158,11,0.06)",   border: "rgba(245,158,11,0.2)" },
            { key: "approved", label: t("returnsManagement.approved"),        count: counts.approved, color: NJIM.success, bg: "rgba(5,150,105,0.06)",   border: "rgba(5,150,105,0.2)" },
            { key: "rejected", label: t("returnsManagement.rejected"),        count: counts.rejected, color: NJIM.danger, bg: "rgba(220,38,38,0.06)",   border: "rgba(220,38,38,0.2)" },
          ];
          return (
            <Grid container spacing={2} mb={3}>
              {cardDefs.map(({ key, label, count, color, bg, border }) => {
                const isActive = activeFilter === key;
                return (
                  <Grid item xs={12} md={3} key={key}>
                    <Card
                      onClick={() => setActiveFilter(key)}
                      sx={{
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        borderRadius: "12px",
                        border: isActive ? `2px solid ${color}` : `1px solid ${NJIM.border}`,
                        background: isActive ? bg : NJIM.white,
                        boxShadow: isActive ? `0 4px 16px ${border}` : "0 1px 3px rgba(26,34,51,0.04)",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: `0 6px 20px ${border}` },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 1.5, color }}>
                        {loading ? "—" : count}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          );
        })()}

        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t("returnsManagement.searchReturns")}
          sx={{
            mt: 2,
            maxWidth: { xs: "100%", md: 360 },
            background: "rgba(248,249,250,0.7)",
          }}
        />

        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: 5 }}>
                <CircularProgress size={44} sx={{ color: NJIM.mint }} />
              </Box>
            ) : (
              <>
                {filteredReturns?.length === 0 ? (
                  <Card
                    sx={{
                      p: 4,
                      borderRadius: "12px",
                      border: `1px solid ${NJIM.border}`,
                      boxShadow: "0 1px 3px rgba(26,34,51,0.04)",
                      textAlign: "center",
                      color: NJIM.textSecondary,
                    }}
                  >
                    {t("common.noData")}
                  </Card>
                ) : (
                  filteredReturns.map((returns) => {
                    const panelId = String(returns.id);
                    const items = returns.items ?? [];
                    return (
                      <Card
                        key={panelId}
                        sx={{
                          borderRadius: "12px !important",
                          overflow: "hidden",
                          border: `1px solid ${NJIM.border}`,
                          boxShadow: "0 1px 3px rgba(26,34,51,0.04)",
                          bgcolor: NJIM.white,
                        }}
                      >
                      <Accordion
                        expanded={expandedReturnId === panelId}
                        onChange={handleExpand(panelId)}
                        disableGutters
                        sx={{
                          boxShadow: "none",
                          bgcolor: "transparent",
                          "&::before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: NJIM.textSecondary }} />}
                          sx={{
                            px: 2.5,
                            py: 1.5,
                            "&:hover": { bgcolor: "rgba(93,183,167,0.06)" },
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1.2,
                            }}
                          >
                            <Box>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: NJIM.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                {t("returnsManagement.returnsNumber")}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: NJIM.textPrimary,
                                  fontSize: "18px",
                                }}
                              >
                                {returns.returnNumber || returns.title}
                              </Typography>
                              <Typography sx={{ color: NJIM.textSecondary, mt: 0.3, fontSize: "13px" }}>
                                {t("common.date")}: {formatDate(returns.requestDate)}
                                {"  •  "}
                                {CURRENCY_SYMBOL}
                                {getDisplayAmount(returns).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                            </Box>
                            <Chip
                              label={getStatusLabel(returns.status)}
                              sx={{
                                fontWeight: 700,
                                borderRadius: "999px",
                                ...getStatusStyles(returns.status),
                              }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2.5 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "18px",
                              color: NJIM.textPrimary,
                              mb: 1.2,
                            }}
                          >
                            {t("returnsManagement.returnDetails")}
                          </Typography>

                          <Table
                            sx={{
                              border: "1px solid rgba(226,232,240,0.5)",
                              borderRadius: "10px",
                              overflow: "hidden",
                              "& .MuiTableCell-root": {
                                padding: "10px 12px",
                                borderBottom: "1px solid rgba(226,232,240,0.35)",
                                fontSize: "13px",
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow sx={{ background: "rgba(248,249,250,0.5)" }}>
                                <TableCell sx={{ fontWeight: 700, color: NJIM.textSecondary }}>{t("salesOrder.productName")}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: NJIM.textSecondary }}>{t("common.quantity")}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: NJIM.textSecondary }}>{t("returnsManagement.batchNumber")}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: NJIM.textSecondary }}>{t("returnsManagement.reason")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} sx={{ textAlign: "center", color: NJIM.textMuted, py: 3 }}>
                                    {t("common.noData")}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                items.map((item, idx) => (
                                  <TableRow key={`${panelId}-${idx}`}>
                                    <TableCell sx={{ color: NJIM.textPrimary, fontWeight: 500 }}>
                                      {productNameMap[String(item.itemId ?? "").trim()] || item.itemId || "-"}
                                    </TableCell>
                                    <TableCell sx={{ color: NJIM.textSecondary }}>
                                      {item.quantity ?? 0}
                                    </TableCell>
                                    <TableCell sx={{ color: NJIM.textSecondary }}>
                                      {item.batchNumber ?? "-"}
                                    </TableCell>
                                    <TableCell sx={{ color: NJIM.textSecondary }}>
                                      {localizeReturnReason(item.reason, t)}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>

                          <Divider sx={{ my: 2 }} />
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "15px",
                              color: NJIM.textPrimary,
                              mb: 0.6,
                            }}
                          >
                            {t("returnsManagement.notes")}
                          </Typography>
                          <Typography sx={{ color: NJIM.textSecondary }}>
                            {returns.additionalNotes || "-"}
                          </Typography>

                          {(returns.rejectionReason || returns.rejectionNote) && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1.2,
                                  p: 1.5,
                                  borderRadius: "10px",
                                  background: "rgba(220,38,38,0.06)",
                                  border: "1px solid rgba(220,38,38,0.18)",
                                }}
                              >
                                <Box sx={{ color: NJIM.danger, flexShrink: 0, mt: "2px", fontSize: 18, lineHeight: 1 }}>
                                  ✕
                                </Box>
                                <Box sx={{ width: "100%" }}>
                                  {returns.rejectionReason && (
                                    <>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "14px",
                                          color: NJIM.danger,
                                          mb: 0.4,
                                        }}
                                      >
                                        {t("workflowPortal.rejectionReason")}
                                      </Typography>
                                      <Typography sx={{ fontSize: "14px", color: NJIM.danger }}>
                                        {returns.rejectionReason}
                                      </Typography>
                                    </>
                                  )}
                                  {returns.rejectionNote && (
                                    <Box sx={{ mt: returns.rejectionReason ? 1.2 : 0 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "14px",
                                          color: NJIM.danger,
                                          mb: 0.4,
                                        }}
                                      >
                                        {t("workflowPortal.rejectionNote", { defaultValue: "Rejection Note" })}
                                      </Typography>
                                      <Typography sx={{ fontSize: "14px", color: NJIM.danger }}>
                                        {returns.rejectionNote}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </>
                          )}
                        </AccordionDetails>
                      </Accordion>
                      </Card>
                    );
                  })
                )}
              </>
            )}
        </Box>
      </Box>

      <InitiateModal
        open={openInitiateModal}
        onClose={() => {
          setOpenInitiateModal(false);
          fetchReturnList();
        }}
      />
    </>
  );
};

export default ReturnsManagement;
